/**
 * Image Slider Block JavaScript
 * Implements prev/next navigation, dot indicators, draggable slides, and autoplay.
 */

(function () {
    'use strict';

    function initImageSlider(container) {
        const root = container || document;
        const blocks = root.querySelectorAll('.image-slider');

        blocks.forEach((block) => {
            if (block.dataset.slider !== 'true') return;
            if (block.dataset.imageSliderInitialized) return;
            block.dataset.imageSliderInitialized = 'true';

            const track = block.querySelector('.image-slider__track');
            const trackWrap = block.querySelector('.image-slider__track-wrap');
            const slides = block.querySelectorAll('.image-slider__slide');
            const dots = block.querySelectorAll('.image-slider__dot');
            const prevBtn = block.querySelector('.image-slider__arrow--prev');
            const nextBtn = block.querySelector('.image-slider__arrow--next');

            if (!track || !trackWrap || slides.length === 0) return;

            let currentIndex = 0;
            const totalSlides = slides.length;
            let autoplayInterval = null;
            const autoplayEnabled = block.dataset.autoplay === 'true';
            const autoplayDelay = parseInt(block.dataset.autoplayDelay || '3000', 10) || 3000;

            function getScrollBounds() {
                const wrapWidth = trackWrap.offsetWidth;
                const trackWidth = track.offsetWidth;
                return { wrapWidth, trackWidth, maxScroll: Math.max(0, trackWidth - wrapWidth) };
            }

            function getCurrentTranslate() {
                const match = track.style.transform?.match(/translateX\(-?(\d+(?:\.\d+)?)px\)/);
                return match ? parseFloat(match[1]) : 0;
            }

            function scrollToSlide(index, immediate = false) {
                if (index < 0) index = 0;
                if (index >= totalSlides) index = totalSlides - 1;
                currentIndex = index;

                const slide = slides[index];
                if (!slide) return;

                const { wrapWidth, trackWidth, maxScroll } = getScrollBounds();
                const slideLeft = slide.offsetLeft;
                const slideWidth = slide.offsetWidth;
                let scrollTarget = slideLeft - (wrapWidth - slideWidth) / 2;
                scrollTarget = Math.max(0, Math.min(scrollTarget, maxScroll));

                track.style.transition = immediate ? 'none' : 'transform 0.4s ease-out';
                track.style.transform = `translateX(-${scrollTarget}px)`;

                // Update dots
                dots.forEach((dot, i) => {
                    const isActive = i === currentIndex;
                    dot.classList.toggle('image-slider__dot--active', isActive);
                    dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
                });
            }

            function goPrev() {
                scrollToSlide(currentIndex - 1);
                resetAutoplay();
            }

            function goNext() {
                scrollToSlide(currentIndex + 1);
                resetAutoplay();
            }

            function startAutoplay() {
                if (!autoplayEnabled || totalSlides <= 1) return;
                stopAutoplay();
                autoplayInterval = setInterval(() => {
                    const next = (currentIndex + 1) % totalSlides;
                    scrollToSlide(next);
                }, autoplayDelay);
            }

            function stopAutoplay() {
                if (autoplayInterval) {
                    clearInterval(autoplayInterval);
                    autoplayInterval = null;
                }
            }

            function resetAutoplay() {
                if (autoplayEnabled) {
                    startAutoplay();
                }
            }

            // Draggable: touch and mouse
            let isDragging = false;
            let startX = 0;
            let startTranslate = 0;

            function onPointerDown(e) {
                if (e.button !== 0 && e.type === 'mousedown') return;
                e.preventDefault(); // Prevent native image drag from blocking our drag
                isDragging = true;
                startX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
                startTranslate = getCurrentTranslate();
                track.style.transition = 'none';
                trackWrap.style.cursor = 'grabbing';
                stopAutoplay();
            }

            function onPointerMove(e) {
                if (!isDragging) return;
                const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
                const deltaX = startX - clientX;
                const { maxScroll } = getScrollBounds();
                let newTranslate = startTranslate + deltaX;
                newTranslate = Math.max(0, Math.min(newTranslate, maxScroll));
                track.style.transform = `translateX(-${newTranslate}px)`;
            }

            function onPointerUp() {
                if (!isDragging) return;
                isDragging = false;
                trackWrap.style.cursor = '';
                track.style.transition = 'transform 0.4s ease-out';

                const currentTranslate = getCurrentTranslate();
                const { wrapWidth } = getScrollBounds();
                let bestIndex = 0;
                let bestDist = Infinity;
                slides.forEach((slide, i) => {
                    const slideLeft = slide.offsetLeft;
                    const slideWidth = slide.offsetWidth;
                    const slideCenter = slideLeft + slideWidth / 2;
                    const viewCenter = currentTranslate + wrapWidth / 2;
                    const dist = Math.abs(slideCenter - viewCenter);
                    if (dist < bestDist) {
                        bestDist = dist;
                        bestIndex = i;
                    }
                });
                scrollToSlide(bestIndex);
                resetAutoplay();
            }

            trackWrap.addEventListener('mousedown', onPointerDown);
            trackWrap.addEventListener('touchstart', onPointerDown, { passive: true });
            document.addEventListener('mousemove', onPointerMove);
            document.addEventListener('touchmove', onPointerMove, { passive: true });
            document.addEventListener('mouseup', onPointerUp);
            document.addEventListener('touchend', onPointerUp);

            // Event listeners
            if (prevBtn) prevBtn.addEventListener('click', goPrev);
            if (nextBtn) nextBtn.addEventListener('click', goNext);

            dots.forEach((dot, index) => {
                dot.addEventListener('click', () => scrollToSlide(index));
            });

            // Keyboard navigation
            block.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    goPrev();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    goNext();
                }
            });

            // Pause autoplay on hover/focus for accessibility
            block.addEventListener('mouseenter', stopAutoplay);
            block.addEventListener('mouseleave', () => autoplayEnabled && startAutoplay());
            block.addEventListener('focusin', stopAutoplay);
            block.addEventListener('focusout', () => autoplayEnabled && startAutoplay());

            // Initial position
            scrollToSlide(0, true);

            // Start autoplay if enabled
            if (autoplayEnabled) startAutoplay();

            // Handle resize
            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => scrollToSlide(currentIndex), 100);
            });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initImageSlider());
    } else {
        initImageSlider();
    }
})();
